import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  getAdminRecentPurchases,
  getTopTokenBalances,
  type RecentPurchaseItem,
} from "../api/adminStatsApi";
import {
  getUsersPaginated,
  getUserDetail,
  updateUserBan,
  type AdminUserSummary,
  type AdminUserDetail,
} from "../api/adminUsersApi";
import {
  searchAdminComments,
  hideAdminComment,
  unhideAdminComment,
  deleteAdminComment,
  type AdminCommentItem,
} from "../api/adminCommentsApi";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import { AdminApiError } from "../api/errors";

const PAGE_SIZE = 10;

type RoleFilter = "all" | "admin" | "user";
type SortFilter = "balanceDesc" | "balanceAsc" | "nameAsc" | "nameDesc";

const sortMap: Record<SortFilter, "zAsc" | "zDesc" | "nameAsc" | "nameDesc"> = {
  balanceDesc: "zDesc",
  balanceAsc: "zAsc",
  nameAsc: "nameAsc",
  nameDesc: "nameDesc",
};

export default function AdminUsersTransactions() {
  const { loading, allowed } = usePermissionCheck("Users.View");
  const [recentPurchases, setRecentPurchases] = useState<RecentPurchaseItem[]>([]);
  const [topBalances, setTopBalances] = useState<Array<{ username: string; balanceZ: number }>>([]);

  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  const [filters, setFilters] = useState<{ search: string; role: RoleFilter; sort: SortFilter }>(
    { search: "", role: "all", sort: "balanceDesc" },
  );
  const [searchInput, setSearchInput] = useState(filters.search);
  const [roleInput, setRoleInput] = useState<RoleFilter>(filters.role);
  const [sortInput, setSortInput] = useState<SortFilter>(filters.sort);
  const [page, setPage] = useState(0);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<AdminUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [userComments, setUserComments] = useState<AdminCommentItem[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);

  const resolveErrorMessage = (error: unknown, fallback: string) =>
    error instanceof AdminApiError ? error.message : fallback;

  useEffect(() => {
    if (!allowed) {
      return;
    }

    const loadStats = async () => {
      const [purchases, balances] = await Promise.all([
        getAdminRecentPurchases(),
        getTopTokenBalances(),
      ]);

      setRecentPurchases(purchases.slice(0, 10));
      setTopBalances(balances.slice(0, 10));
    };

    loadStats();
  }, [allowed]);

  useEffect(() => {
    if (!allowed) {
      return;
    }

    const loadUsers = async () => {
      setLoadingUsers(true);
      setUsersError(null);
      try {
        const query = await getUsersPaginated({
          search: filters.search || undefined,
          role: filters.role === "all" ? undefined : filters.role,
          sort: sortMap[filters.sort],
          skip: page * PAGE_SIZE,
          take: PAGE_SIZE,
        });
        setUsers(query.items);
        setTotalUsers(query.totalCount);
      } catch (error) {
        setUsers([]);
        setTotalUsers(0);
        setUsersError(resolveErrorMessage(error, "Kullanıcı listesi yüklenemedi."));
      } finally {
        setLoadingUsers(false);
      }
    };

    void loadUsers();
  }, [allowed, filters, page]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalUsers / PAGE_SIZE));
  }, [totalUsers]);

  const applyFilters = () => {
    setFilters({ search: searchInput.trim(), role: roleInput, sort: sortInput });
    setPage(0);
  };

  const openUserDetail = async (userId: string) => {
    setSelectedUserId(userId);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const detail = await getUserDetail(userId);
      setSelectedDetail(detail);
      if (detail) {
        await loadUserComments(detail.username);
      } else {
        setUserComments([]);
      }
    } catch (error) {
      setSelectedDetail(null);
      setUserComments([]);
      setDetailError(resolveErrorMessage(error, "Kullanıcı detayı getirilemedi."));
    } finally {
      setDetailLoading(false);
    }
  };

  const loadUserComments = async (username: string) => {
    setCommentsLoading(true);
    setCommentsError(null);
    try {
      const response = await searchAdminComments({ username, take: 20 });
      setUserComments(response.items);
    } catch (error) {
      setUserComments([]);
      setCommentsError(resolveErrorMessage(error, "Kullanıcı yorumları yüklenemedi."));
    } finally {
      setCommentsLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedUserId(null);
    setSelectedDetail(null);
    setUserComments([]);
    setDetailError(null);
    setCommentsError(null);
  };

  const toggleBan = async (field: "isBannedAll" | "isCommentBanned", value: boolean) => {
    if (!selectedDetail) {
      return;
    }

    const payload = field === "isBannedAll" ? { isBannedAll: value } : { isCommentBanned: value };
    const success = await updateUserBan(selectedDetail.id, payload);
    if (!success) {
      Swal.fire("Error", "Failed to update ban status.", "error");
      return;
    }

    setSelectedDetail({ ...selectedDetail, ...payload });
    Swal.fire("Saved", "User restrictions updated.", "success");
  };

  const handleCommentAction = async (commentId: string, action: "hide" | "unhide" | "delete") => {
    let success = false;
    if (action === "hide") {
      success = await hideAdminComment(commentId);
    } else if (action === "unhide") {
      success = await unhideAdminComment(commentId);
    } else {
      success = await deleteAdminComment(commentId);
    }

    if (!success) {
      Swal.fire("Error", "Comment action failed.", "error");
      return;
    }

    if (selectedDetail) {
      await loadUserComments(selectedDetail.username);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-sm text-gray-500">Checking permissions...</div>;
  }

  if (!allowed) {
    return <div className="p-8 text-center text-sm text-red-500">Bu sayfaya erişim yetkin yok.</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Users & Transactions</h1>

      <section className="grid gap-4 xl:grid-cols-2">
        <div className="rounded bg-white p-4 text-sm shadow">
          <div className="mb-3 font-medium">Recent Purchases</div>
          {recentPurchases.length === 0 && <div className="text-gray-400">No purchases recorded.</div>}
          {recentPurchases.length > 0 && (
            <div className="divide-y">
              {recentPurchases.map((purchase, idx) => (
                <div key={`${purchase.username}-${idx}`} className="flex items-center justify-between py-2">
                  <span className="w-1/4 truncate pr-2 text-sm font-medium text-gray-700">{purchase.username}</span>
                  <span className="w-1/4 text-xs uppercase text-gray-500">{purchase.itemType}</span>
                  <span className="w-1/4 text-sm text-gray-600">{purchase.amountZ} Z</span>
                  <span className="w-1/4 text-right text-xs text-gray-500">{new Date(purchase.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded bg-white p-4 text-sm shadow">
          <div className="mb-3 font-medium">Top Token Balances</div>
          {topBalances.length === 0 && <div className="text-gray-400">No balance data.</div>}
          {topBalances.length > 0 && (
            <div className="space-y-2">
              {topBalances.map((entry) => (
                <div key={entry.username} className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">{entry.username}</span>
                  <span className="text-gray-500">{entry.balanceZ} Z</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="space-y-4 rounded bg-white p-4 shadow">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="font-medium">User Directory</div>
            <p className="text-xs text-gray-500">Search, filter, and inspect individual users.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-600">
              <div className="mb-1">Search</div>
              <input
                className="w-48 rounded border border-gray-300 p-2 text-sm"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="username or email"
              />
            </label>
            <label className="text-xs text-gray-600">
              <div className="mb-1">Role</div>
              <select
                className="w-32 rounded border border-gray-300 p-2 text-sm"
                value={roleInput}
                onChange={(e) => setRoleInput(e.target.value as RoleFilter)}
              >
                <option value="all">All</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </label>
            <label className="text-xs text-gray-600">
              <div className="mb-1">Sort</div>
              <select
                className="w-36 rounded border border-gray-300 p-2 text-sm"
                value={sortInput}
                onChange={(e) => setSortInput(e.target.value as SortFilter)}
              >
                <option value="balanceDesc">Balance high to low</option>
                <option value="balanceAsc">Balance low to high</option>
                <option value="nameAsc">Name A to Z</option>
                <option value="nameDesc">Name Z to A</option>
              </select>
            </label>
            <button
              onClick={applyFilters}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>

        {usersError && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
            {usersError}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Balance</th>
                <th className="px-3 py-2">Bans</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingUsers && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-sm text-gray-500">
                    Loading users...
                  </td>
                </tr>
              )}
              {!loadingUsers && users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-sm text-gray-500">
                    {usersError ? "Unable to load users." : "No users match the current filters."}
                  </td>
                </tr>
              )}
              {!loadingUsers && users.map((user) => (
                <tr key={user.id} className="border-b last:border-none">
                  <td className="px-3 py-2">
                    <div className="text-sm font-medium text-gray-800">{user.username}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-600">{user.role}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{user.zTokenBalance} Z</td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {user.isBannedAll ? "Full ban" : "-"}
                    {user.isCommentBanned ? " | Comment" : ""}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => openUserDetail(user.id)}
                      className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                    >
                      View details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            Page {page + 1} of {totalPages} | {totalUsers} users
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {selectedUserId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded bg-white p-6 shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {selectedDetail?.username || "User detail"}
                </h2>
                <p className="text-xs text-gray-500">{selectedDetail?.email}</p>
              </div>
              <button onClick={closeDetail} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
            </div>

            {detailLoading && <div className="py-6 text-center text-sm text-gray-500">Loading user data...</div>}

            {!detailLoading && detailError && (
              <div className="py-4 text-center text-sm text-red-500">{detailError}</div>
            )}

            {!detailLoading && selectedDetail && (
              <div className="space-y-6 pt-4">
                <section className="grid gap-4 md:grid-cols-2">
                  <div className="rounded border border-gray-200 p-4 text-sm">
                    <div className="font-medium text-gray-700">Account</div>
                    <div className="mt-2 space-y-1 text-gray-600">
                      <div>Role: {selectedDetail.role}</div>
                      <div>Balance: {selectedDetail.zTokenBalance} Z</div>
                      <div>Created: {new Date(selectedDetail.createdAt).toLocaleString()}</div>
                    </div>
                    <div className="mt-3 space-y-2 text-xs text-gray-600">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedDetail.isBannedAll}
                          onChange={(e) => toggleBan("isBannedAll", e.target.checked)}
                        />
                        Full platform ban
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedDetail.isCommentBanned}
                          onChange={(e) => toggleBan("isCommentBanned", e.target.checked)}
                        />
                        Comment ban
                      </label>
                    </div>
                  </div>

                  <div className="rounded border border-gray-200 p-4 text-sm">
                    <div className="font-medium text-gray-700">Progress</div>
                    {selectedDetail.progress ? (
                      <div className="mt-2 space-y-1 text-gray-600">
                        <div>Level: {selectedDetail.progress.level}</div>
                        <div>Exp: {selectedDetail.progress.currentExp} / {selectedDetail.progress.nextLevelExp}</div>
                        <div>Total reads: {selectedDetail.progress.totalReads}</div>
                        <div>Total comments: {selectedDetail.progress.totalComments}</div>
                        <div>Total purchases: {selectedDetail.progress.totalPurchases}</div>
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-gray-500">No progress data.</div>
                    )}
                  </div>
                </section>

                <section>
                  <div className="mb-2 text-sm font-medium text-gray-700">Active Packages</div>
                  {selectedDetail.activePackages.length === 0 && (
                    <div className="text-xs text-gray-500">No active packages.</div>
                  )}
                  {selectedDetail.activePackages.length > 0 && (
                    <div className="space-y-2 text-xs text-gray-600">
                      {selectedDetail.activePackages.map((pkg) => (
                        <div key={pkg.id} className="rounded border border-gray-200 p-3">
                          <div className="font-medium text-gray-700">{pkg.packageName}</div>
                          <div>From {new Date(pkg.startDateUtc).toLocaleDateString()} to {new Date(pkg.endDateUtc).toLocaleDateString()}</div>
                          {pkg.dailyQuotaPremium !== undefined && (
                            <div>Daily premium quota: {pkg.dailyQuotaPremium}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section>
                  <div className="mb-2 text-sm font-medium text-gray-700">Recent Unlocks</div>
                  {selectedDetail.recentUnlocks.length === 0 && (
                    <div className="text-xs text-gray-500">No unlock history.</div>
                  )}
                  {selectedDetail.recentUnlocks.length > 0 && (
                    <ul className="space-y-2 text-xs text-gray-600">
                      {selectedDetail.recentUnlocks.map((unlock) => (
                        <li key={unlock.id} className="rounded border border-gray-200 p-2">
                          {unlock.partTitle} - {unlock.price} Z on {new Date(unlock.purchasedAt).toLocaleString()}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section>
                  <div className="mb-2 text-sm font-medium text-gray-700">Recent Transactions</div>
                  {selectedDetail.recentTransactions.length === 0 && (
                    <div className="text-xs text-gray-500">No transactions recorded.</div>
                  )}
                  {selectedDetail.recentTransactions.length > 0 && (
                    <ul className="space-y-2 text-xs text-gray-600">
                      {selectedDetail.recentTransactions.map((tx) => (
                        <li key={tx.id} className="rounded border border-gray-200 p-2">
                          {tx.type} - {tx.amount} Z on {new Date(tx.createdAt).toLocaleString()}
                          {tx.description && <div className="text-[10px] text-gray-500">{tx.description}</div>}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section>
                  <div className="mb-2 text-sm font-medium text-gray-700">Recent Comments</div>
                  {commentsError && (
                    <div className="mb-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                      {commentsError}
                    </div>
                  )}
                  {commentsLoading && <div className="text-xs text-gray-500">Loading comments...</div>}
                  {!commentsLoading && userComments.length === 0 && (
                    <div className="text-xs text-gray-500">No comments found.</div>
                  )}
                  {!commentsLoading && userComments.length > 0 && (
                    <div className="space-y-3 text-xs text-gray-600">
                      {userComments.map((comment) => (
                        <div key={comment.id} className="rounded border border-gray-200 p-3">
                          <div className="text-[11px] text-gray-500">{new Date(comment.createdAt).toLocaleString()}</div>
                          <div className="mt-1 text-sm text-gray-700">{comment.text}</div>
                          <div className="mt-2 flex gap-2">
                            <button
                              onClick={() => handleCommentAction(comment.id, comment.isHidden ? "unhide" : "hide")}
                              className="rounded bg-amber-500 px-3 py-1 text-xs text-white hover:bg-amber-600"
                            >
                              {comment.isHidden ? "Unhide" : "Hide"}
                            </button>
                            <button
                              onClick={() => handleCommentAction(comment.id, "delete")}
                              className="rounded bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
